import { zodResolver } from '@hookform/resolvers/zod';
import { Button, DialogActions, DialogContent, DialogTitle, Grid, Tooltip, Typography } from '@mui/material';
import { useUuid } from '@procureai/ui-mui';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  negotiationCaseCreateFormDataDefault,
  negotiationCaseCreateFormDataSchema,
  NegotiationCaseCreateFormData,
} from '../../common/common.schema';
import { TsrctQuoteMode } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { DialogActionsLayout } from '~/components/DialogWizard/components/DialogActionsLayout/DialogActionsLayout';
import { NegotiationTermsForm } from '~/components/NegotiationTermsForm/NegotiationTermsForm';
import { SupplierContactForm } from '~/modules/NegotiationSupplierContactWidget/components/SupplierContactForm/SupplierContactForm';

import { useLegacyNegotiationCaseCreation } from './LegacyNegotiationCaseCreation.hooks';

export interface DemandCaseAddProps {
  closeDialog: () => void;
}

export const NegotiationCaseCreateSimpleDialog = ({ closeDialog }: DemandCaseAddProps) => {
  const form = useForm<NegotiationCaseCreateFormData>({
    mode: 'onTouched',
    resolver: zodResolver(negotiationCaseCreateFormDataSchema),
    defaultValues: negotiationCaseCreateFormDataDefault,
  });
  const formId = useUuid();

  const { t: tGeneralActions } = useTranslation(I18nNamespace.common, { keyPrefix: 'DialogWizard.general.actions' });
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'DemandAddSupplierDialog' });

  const { createNegotiation, loading, releaseNegotiation } = useLegacyNegotiationCaseCreation();

  const quoteMode = form.watch('options.quoteMode');
  const isBidMode = quoteMode === TsrctQuoteMode.BID_SHEET || quoteMode === TsrctQuoteMode.BID_SHEET_PROPOSAL;

  const handleDraftSubmit = async (data: NegotiationCaseCreateFormData) => {
    await createNegotiation(data);
    closeDialog();
  };

  const handleReleaseSubmit = async (data: NegotiationCaseCreateFormData) => {
    const response = await createNegotiation(data);
    const caseId = response?.data?.tesseract.demand.negotiationCase.create.id;
    if (caseId) {
      await releaseNegotiation({ id: caseId });
    }
    closeDialog();
  };

  return (
    <>
      <DialogTitle>{t('title')}</DialogTitle>
      <DialogContent>
        <FormProvider {...form}>
          <form id={formId} autoComplete="off" noValidate>
            <Grid container spacing={6} columns={2}>
              <Grid size={2}>
                <Typography>{t('description')}</Typography>
              </Grid>
              <Grid size={1}>
                <SupplierContactForm />
              </Grid>
              <Grid size={1}>
                <NegotiationTermsForm />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <DialogActionsLayout
          left={<Button onClick={closeDialog}>{tGeneralActions('cancel')}</Button>}
          right={
            <>
              <Button
                onClick={form.handleSubmit(handleDraftSubmit)}
                loading={loading}
                form={formId}
                type="submit"
                variant="outlined"
              >
                {t('actions.saveDraft')}
              </Button>
              <Tooltip title={!isBidMode ? t('button.disabledByQuoteMode') : undefined}>
                <span>
                  <Button
                    onClick={form.handleSubmit(handleReleaseSubmit)}
                    loading={loading}
                    disabled={!isBidMode}
                    form={formId}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    {t('actions.sendToSupplier')}
                  </Button>
                </span>
              </Tooltip>
            </>
          }
        />
      </DialogActions>
    </>
  );
};
